import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type Money } from "@src/appV2/lib/Money/types";
import {
  getShiftDurationHours,
  getSpendLimitStateForCharges,
  type SpendLimitCharge,
} from "@src/appV2/FacilityDashboard/VariableCharge/hooks/variableChargeSpendLimit";
import { useVariableChargeSpendLimit } from "@src/appV2/redesign/SpendingControls/api/useVariableChargeSpendLimit";
import {
  useBoostLimitIncreaseRequest,
  type UseBoostLimitIncreaseRequestResult,
} from "@src/appV2/redesign/SpendingControls/hooks/useBoostLimitIncreaseRequest";
import { getWeekStartForDate } from "@src/appV2/redesign/SpendingControls/utils/spendingLimitRequest";
import { type UseModalState } from "@src/lib/ui-react";
import { useMemo } from "react";

import { majorToMinor } from "@src/appV2/lib/Money/utils/minorMajor";

interface UseInviteBoostSpendLimitParams {
  workplaceId: string;
  qualification: string;
  startTime: Date;
  endTime: Date;
  baseChargeRate: Money | undefined;
  /** Extra hourly charge (boost) in dollars. */
  customChargeRateBoost: number | undefined;
  /**
   * Total shift duration in hours the boost applies to in this week. Defaults to the single shift's
   * duration (start→end). Callers inviting several shifts in one week pass the summed hours, so the
   * weekly projection (`boost × totalDurationInHours`) is exact, not a worst-case longest-shift × count.
   */
  totalDurationInHours?: number;
  enabled: boolean;
  timeZone: string;
  /**
   * Charge-rate dialog's modal state — closed when the request opens, reopened on Back. Omit on
   * inline surfaces with no editor dialog to hide/restore; the request modal then opens standalone.
   */
  editModalState?: UseModalState;
  /**
   * Whether to emit the "spend limit exceeded" analytics event (default true). The classic multi-week
   * flow sets this only on the display-week probe so the event fires once per invite attempt, not once
   * per over-limit week.
   */
  logExceededEvent?: boolean;
}

interface UseInviteBoostSpendLimitResult {
  isActive: boolean;
  exceeded: boolean;
  overageInMinorUnits: number;
  isLoading: boolean;
  limitInMinorUnits: number | undefined;
  weekStartDate: Date;
  increaseRequest: UseBoostLimitIncreaseRequestResult;
  isAdmin: boolean;
  isChecking: boolean;
  /** Send/Save gate: over the limit or still checking, and only for workplace users — admins are warned,
   * never blocked (SYN-2386). */
  blocksSubmit: boolean;
}

/**
 * variableCharge spend-limit advisory for the custom-rate invite flow, shared by every invite
 * surface (redesign and classic). Composes the shared variableCharge spend-limit machinery; the projected weekly spend
 * bump is `boost × totalDurationInHours` (the base rate cancels in the delta).
 */
export function useInviteBoostSpendLimit(
  params: UseInviteBoostSpendLimitParams
): UseInviteBoostSpendLimitResult {
  const {
    workplaceId,
    qualification,
    startTime,
    endTime,
    baseChargeRate,
    customChargeRateBoost,
    totalDurationInHours,
    enabled,
    timeZone,
    editModalState,
    logExceededEvent = true,
  } = params;

  const weekStartDate = useMemo(
    () => getWeekStartForDate(startTime, timeZone),
    [startTime, timeZone]
  );

  const isAdmin = Boolean(useSession().admin);

  const { isEnabled, limitInMinorUnits, usageInMinorUnits, controlUpdatedAt, isLoading } =
    useVariableChargeSpendLimit({ workplaceId, weekStartDate, enabled });

  const increaseRequest = useBoostLimitIncreaseRequest({
    workplaceId,
    weekStartDate,
    controlUpdatedAt,
    editModalState,
  });

  const durationHours = useMemo(
    () =>
      totalDurationInHours ??
      getShiftDurationHours({ start: startTime.toISOString(), end: endTime.toISOString() }),
    [totalDurationInHours, startTime, endTime]
  );

  const isActive = isEnabled && isDefined(baseChargeRate);

  const spendLimitState = useMemo(() => {
    if (!isEnabled || !isDefined(baseChargeRate)) {
      return { exceeded: false, overageInMinorUnits: 0 };
    }

    const charge: SpendLimitCharge = {
      qualification,
      initialRateInMinorUnits: baseChargeRate.amountInMinorUnits,
      currentRateInMinorUnits:
        baseChargeRate.amountInMinorUnits + majorToMinor(customChargeRateBoost ?? 0),
    };

    return getSpendLimitStateForCharges({
      charges: [charge],
      durationHours,
      shiftCount: 1,
      currentUsageInMinorUnits: usageInMinorUnits,
      limitInMinorUnits,
    });
  }, [
    isEnabled,
    baseChargeRate,
    qualification,
    customChargeRateBoost,
    durationHours,
    usageInMinorUnits,
    limitInMinorUnits,
  ]);

  useLogEffect(
    APP_V2_USER_EVENTS.SCHEDULE_INVITE_SPEND_LIMIT_EXCEEDED,
    {
      workplaceId,
      qualification,
      overageInMinorUnits: spendLimitState.overageInMinorUnits,
    },
    { enabled: spendLimitState.exceeded && logExceededEvent }
  );

  const isChecking = isActive && (customChargeRateBoost ?? 0) > 0 && isLoading;
  const blocksSubmit = !isAdmin && (spendLimitState.exceeded || isChecking);

  return {
    isActive,
    exceeded: spendLimitState.exceeded,
    overageInMinorUnits: spendLimitState.overageInMinorUnits,
    isLoading,
    limitInMinorUnits,
    weekStartDate,
    increaseRequest,
    isAdmin,
    isChecking,
    blocksSubmit,
  };
}
